import { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, Shield, Activity, X, Eye, EyeOff, Download, Filter } from 'lucide-react';
import { Loader } from './ui/Loader';
import { toast } from '../utils/toast';
import { API_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { fetchWithAuth } from '../utils/api';

interface SubAdmin {
  id: string;
  username: string;
  display_name: string | null;
  permissions: string[];
  is_active: boolean;
  last_login: string | null;
  created_at: string;
}

interface ActivityLog {
  id: string;
  action: string;
  module: string;
  target_id: string | null;
  details: any;
  timestamp: string;
  admin_users: {
    username: string;
    display_name: string | null;
  };
}

interface AdminAccessControlProps {
  accessToken: string;
}

const MODULE_LABELS: Record<string, string> = {
  revenue: '📊 Inntekt',
  users: '👥 Bruker-oversikt',
  employers: '✅ Godkjenning av arbeidsgivere',
  reports: '🚨 Rapporter',
  refunds: '↩️ Angreretter',
  emails: '📧 E-postlister'
};

const ALL_MODULES = Object.keys(MODULE_LABELS);

export function AdminAccessControl({ accessToken }: AdminAccessControlProps) {
  const adminModalZ = useAdminModalZIndex();
  const [subAdmins, setSubAdmins] = useState<SubAdmin[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<SubAdmin | null>(null);
  const [maxAdmins, setMaxAdmins] = useState(10);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    displayName: '',
    permissions: [] as string[]
  });
  const [showPassword, setShowPassword] = useState(false);

  // Export filter state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: '',
    endDate: '',
    adminId: '',
    actionType: '',
    module: ''
  });
  const [exporting, setExporting] = useState(false);

  // ✅ NEW: Delete log confirmation state
  const [showDeleteLogConfirm, setShowDeleteLogConfirm] = useState(false);
  const [logToDelete, setLogToDelete] = useState<string | null>(null);
  const [deletingLog, setDeletingLog] = useState(false);

  useEffect(() => {
    fetchSubAdmins();
    fetchActivityLog();
  }, []);

  const fetchSubAdmins = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/list`,
        {}
      );

      if (response.ok) {
        const data = await response.json();
        setSubAdmins(data.admins || []);
        setMaxAdmins(data.max_admins || 10);
      }
    } catch (error) {
      devError('Error fetching sub-admins:', error);
      toast.error('Kunne ikke hente sub-admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/activity-log?limit=20`,
        {}
      );

      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data.logs || []);
      }
    } catch (error) {
      devError('Error fetching activity log:', error);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      // Validation
      if (!formData.username || !formData.password) {
        toast.error('Brukernavn og passord er påkrevd');
        return;
      }

      if (formData.permissions.length === 0) {
        toast.error('Velg minst én modul');
        return;
      }

      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            display_name: formData.displayName || null,
            permissions: formData.permissions
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Sub-admin opprettet!');
        setShowCreateModal(false);
        resetForm();
        fetchSubAdmins();
        fetchActivityLog();
      } else {
        toast.error(data.error || 'Kunne ikke opprette sub-admin');
      }
    } catch (error) {
      devError('Error creating sub-admin:', error);
      toast.error('Kunne ikke opprette sub-admin');
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      const updates: any = {
        admin_id: selectedAdmin.id,
        username: formData.username,
        display_name: formData.displayName || null,
        permissions: formData.permissions,
        is_active: selectedAdmin.is_active
      };

      // Only include password if changed
      if (formData.password) {
        updates.password = formData.password;
      }

      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success('Sub-admin oppdatert!');
        setShowEditModal(false);
        setSelectedAdmin(null);
        resetForm();
        fetchSubAdmins();
        fetchActivityLog();
      } else {
        toast.error(data.error || 'Kunne ikke oppdatere sub-admin');
      }
    } catch (error) {
      devError('Error updating sub-admin:', error);
      toast.error('Kunne ikke oppdatere sub-admin');
    }
  };

  const handleToggleActive = async (admin: SubAdmin) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            admin_id: admin.id,
            is_active: !admin.is_active
          })
        }
      );

      if (response.ok) {
        toast.success(admin.is_active ? 'Sub-admin deaktivert' : 'Sub-admin aktivert');
        fetchSubAdmins();
        fetchActivityLog();
      } else {
        toast.error('Kunne ikke endre status');
      }
    } catch (error) {
      devError('Error toggling active:', error);
      toast.error('Kunne ikke endre status');
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!confirm('Er du sikker på at du vil slette denne sub-admin?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/delete`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ admin_id: adminId })
        }
      );

      if (response.ok) {
        toast.success('Sub-admin slettet');
        fetchSubAdmins();
        fetchActivityLog();
      } else {
        toast.error('Kunne ikke slette sub-admin');
      }
    } catch (error) {
      devError('Error deleting sub-admin:', error);
      toast.error('Kunne ikke slette sub-admin');
    }
  };

  const openEditModal = (admin: SubAdmin) => {
    setSelectedAdmin(admin);
    setFormData({
      username: admin.username,
      password: '', // Don't populate password
      displayName: admin.display_name || '',
      permissions: admin.permissions
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      displayName: '',
      permissions: []
    });
    setShowPassword(false);
  };

  const togglePermission = (module: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(module)
        ? prev.permissions.filter(p => p !== module)
        : [...prev.permissions, module]
    }));
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'created_sub_admin': 'Opprettet sub-admin',
      'updated_sub_admin': 'Oppdatert sub-admin',
      'deleted_sub_admin': 'Slettet sub-admin',
      'logged_in': 'Logget inn',
      'approved_employer': 'Godkjente arbeidsgiver',
      'rejected_employer': 'Avviste arbeidsgiver',
      'handled_report': 'Behandlet rapport',
      'viewed_revenue': 'Så inntektsdata'
    };
    return labels[action] || action;
  };

  const handleExportLog = async () => {
    setExporting(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (exportFilters.startDate) params.append('start_date', exportFilters.startDate);
      if (exportFilters.endDate) params.append('end_date', exportFilters.endDate);
      if (exportFilters.adminId) params.append('admin_id', exportFilters.adminId);
      if (exportFilters.actionType) params.append('action_type', exportFilters.actionType);
      if (exportFilters.module) params.append('module', exportFilters.module);

      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/activity-log/export?${params.toString()}`,
        {}
      );

      if (response.ok) {
        // Get CSV blob
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `aktivitetslogg_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success('Aktivitetslogg lastet ned!');
        setShowExportModal(false);
      } else {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke eksportere logg');
      }
    } catch (error) {
      devError('Error exporting log:', error);
      toast.error('Kunne ikke eksportere logg');
    } finally {
      setExporting(false);
    }
  };

  // ✅ NEW: Delete activity log function
  const handleDeleteLog = async () => {
    if (!logToDelete) return;

    setDeletingLog(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/sub-admins/activity-log/${logToDelete}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        toast.success('Logg slettet');
        fetchActivityLog(); // Refresh the log
      } else {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke slette logg');
      }
    } catch (error) {
      devError('Error deleting log:', error);
      toast.error('Kunne ikke slette logg');
    } finally {
      setDeletingLog(false);
      setShowDeleteLogConfirm(false);
      setLogToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="lg" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl flex items-center gap-2">
            <Users className="w-6 h-6 text-purple-600" />
            Tilgangsstyring
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Administrer sub-admins og deres tilganger ({subAdmins.length}/{maxAdmins})
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          disabled={subAdmins.length >= maxAdmins}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
          Legg til admin
        </button>
      </div>

      {/* Sub-Admins List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bruker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tilganger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sist pålogget
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {subAdmins.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    Ingen sub-admins opprettet ennå
                  </td>
                </tr>
              ) : (
                subAdmins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {admin.display_name || admin.username}
                        </div>
                        {admin.display_name && (
                          <div className="text-sm text-gray-500">@{admin.username}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {admin.permissions.map((perm) => (
                          <span
                            key={perm}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-100 text-purple-700"
                          >
                            {MODULE_LABELS[perm]?.split(' ')[0]}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {admin.is_active ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                          🟢 Aktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                          🔴 Deaktivert
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {admin.last_login ? formatTimestamp(admin.last_login) : 'Aldri'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(admin)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Rediger"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(admin)}
                          className={`p-2 rounded-lg transition-colors ${
                            admin.is_active
                              ? 'text-orange-600 hover:bg-orange-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={admin.is_active ? 'Deaktiver' : 'Aktiver'}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Slett"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activity Log */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-purple-600" />
          Aktivitetslogg
        </h3>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {activityLogs.length === 0 ? (
            <p className="text-gray-500 text-sm">Ingen aktivitet ennå</p>
          ) : (
            activityLogs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {log.admin_users.display_name || log.admin_users.username}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="text-sm text-gray-600">
                      {getActionLabel(log.action)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {formatTimestamp(log.timestamp)}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setLogToDelete(log.id);
                    setShowDeleteLogConfirm(true);
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Slett logg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
        <div className="mt-4">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Eksporter logg
          </button>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl">Opprett ny admin</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brukernavn *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="ola_nordmann"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visningsnavn
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="Ola Nordmann"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passord *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min. 8 tegn, 1 tall"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tilgang til moduler *
                </label>
                <div className="space-y-2">
                  {ALL_MODULES.map((module) => (
                    <label key={module} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(module)}
                        onChange={() => togglePermission(module)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm">{MODULE_LABELS[module]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleCreateAdmin}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Opprett admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl">Rediger admin</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedAdmin(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brukernavn *
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visningsnavn
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nytt passord (la stå tom for å beholde)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min. 8 tegn, 1 tall"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tilgang til moduler *
                </label>
                <div className="space-y-2">
                  {ALL_MODULES.map((module) => (
                    <label key={module} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(module)}
                        onChange={() => togglePermission(module)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm">{MODULE_LABELS[module]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedAdmin(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleUpdateAdmin}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Lagre endringer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl">Eksporter aktivitetslogg</h3>
              <button
                onClick={() => {
                  setShowExportModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Startdato
                </label>
                <input
                  type="date"
                  value={exportFilters.startDate}
                  onChange={(e) => setExportFilters({ ...exportFilters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sluttdato
                </label>
                <input
                  type="date"
                  value={exportFilters.endDate}
                  onChange={(e) => setExportFilters({ ...exportFilters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin-ID
                </label>
                <input
                  type="text"
                  value={exportFilters.adminId}
                  onChange={(e) => setExportFilters({ ...exportFilters, adminId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Handlingstype
                </label>
                <input
                  type="text"
                  value={exportFilters.actionType}
                  onChange={(e) => setExportFilters({ ...exportFilters, actionType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modul
                </label>
                <select
                  value={exportFilters.module}
                  onChange={(e) => setExportFilters({ ...exportFilters, module: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Alle moduler</option>
                  {ALL_MODULES.map((module) => (
                    <option key={module} value={module}>
                      {MODULE_LABELS[module]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowExportModal(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleExportLog}
                  disabled={exporting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {exporting ? 'Eksporterer...' : 'Eksporter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Log Confirmation */}
      {showDeleteLogConfirm && (
        <ConfirmDialog
          isOpen={true}
          title="Slett aktivitetslogg"
          message="Er du sikker på at du vil slette denne aktivitetsloggen?"
          confirmText="Slett"
          cancelText="Avbryt"
          variant="danger"
          onConfirm={handleDeleteLog}
          onClose={() => {
            setShowDeleteLogConfirm(false);
            setLogToDelete(null);
          }}
          loading={deletingLog}
          loadingText="Sletter..."
          portalZIndex={adminModalZ}
        />
      )}
    </div>
  );
}
